/**
 * Экран подтверждения email `/verify` (Фаза 3, И3).
 *
 * Токен подтверждения приходит в query `?token=…` (ссылка из письма); `App`
 * извлекает его и передаёт пропом `token` (как `/reset`). На mount при наличии
 * токена шлём `POST /api/auth/verify {token}` через [`verifyEmail`](../useAuth.tsx):
 * успех → «Email подтверждён» + ссылка на главную; при этом `user.verified`
 * проставляется в `true` (если браузер залогинен). Нет токена / ошибка (невалидный,
 * просроченный, использованный) → понятное сообщение + возможность отправить письмо
 * снова (если есть сессия) либо ссылка на `/login`.
 *
 * Состояния: загрузка / успех / ошибка / нет-токена.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '../components/ui';
import { useAuth } from '../useAuth';

type VerifyStatus = 'loading' | 'success' | 'error' | 'none';

export interface VerifyProps {
  /** Токен подтверждения (из `?token=`). Если нет — экран покажет «нет токена». */
  token?: string;
  /** Вызывается после успешного подтверждения (обычно — редирект на главную). */
  onSuccess?: () => void;
}

export function Verify({ token, onSuccess }: VerifyProps): JSX.Element {
  const { t } = useTranslation();
  const { user, verifyEmail, resendVerification } = useAuth();
  const [status, setStatus] = useState<VerifyStatus>(token ? 'loading' : 'none');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('none');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    verifyEmail(token)
      .then((res) => {
        if (cancelled) return;
        setStatus(res.status === 'ok' ? 'success' : 'error');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [token, verifyEmail]);

  async function handleResend(): Promise<void> {
    if (resending) return;
    setResending(true);
    setResendMsg(null);
    try {
      const res = await resendVerification();
      setResendMsg(
        res.status === 'already verified' ? t('verify.alreadyVerified') : t('verify.resent'),
      );
    } catch {
      setResendMsg(t('verify.resendFailed'));
    } finally {
      setResending(false);
    }
  }

  return (
    <main className="card card--page" aria-labelledby="verify-title">
      <h1 id="verify-title" className="page-title">
        {t('verify.title')}
      </h1>

      {status === 'loading' ? (
        <p className="muted" data-testid="verify-loading">
          {t('verify.loading')}
        </p>
      ) : null}

      {status === 'success' ? (
        <div data-testid="verify-success">
          <p className="muted">{t('verify.success')}</p>
          <p className="auth-switch">
            <button
              type="button"
              className="link-btn"
              onClick={() => onSuccess?.()}
              data-testid="verify-go-home"
            >
              {t('nav.toMain')}
            </button>
          </p>
        </div>
      ) : null}

      {status === 'error' ? (
        <div data-testid="verify-error">
          <p className="error" role="alert">
            {t('verify.errorInvalid')}
          </p>
          {user ? (
            <p className="auth-switch">
              <button
                type="button"
                className="link-btn"
                onClick={() => void handleResend()}
                disabled={resending}
                data-testid="verify-resend"
              >
                {resending ? t('common.submitting') : t('common.resendEmail')}
              </button>
            </p>
          ) : (
            <p className="auth-switch">
              <Link to="/login">{t('nav.login')}</Link>
              {t('verify.loginResendSuffix')}
            </p>
          )}
          {resendMsg ? <p className="field-hint">{resendMsg}</p> : null}
        </div>
      ) : null}

      {status === 'none' ? (
        <div data-testid="verify-no-token">
          <p className="error" role="alert">
            {t('verify.errorIncomplete')}
          </p>
          <p className="auth-switch">
            {user ? (
              <button
                type="button"
                className="link-btn"
                onClick={() => void handleResend()}
                disabled={resending}
                data-testid="verify-resend"
              >
                {resending ? t('common.submitting') : t('common.resendEmail')}
              </button>
            ) : (
              <Link to="/login">{t('nav.login')}</Link>
            )}
          </p>
        </div>
      ) : null}

      <p className="auth-switch">
        <Link to="/">{t('nav.toMain')}</Link>
      </p>
    </main>
  );
}
